// Admin research-band readout (main.ts "research mode"): a second live
// ratio / BTC price / depth block for a non-default band, shown beside the
// product readout. Deliberately no R-HOD/R-LOD and no 24h stats -- those
// are server-computed default-band values with no research-band counterpart.
import { el } from "../util/dom";
import { formatBtc, formatPrice } from "../util/format";
import { formatDisplayRatio } from "../displayMode";
import { isEntitled } from "../api/entitlement";
import { renderStreamStatus, setLiveValue, setLockedValue } from "./dashboardView";
import type { StreamStatus } from "../api/stream";
import type { Tick } from "../api/types";

export interface BandReadoutView {
  root: HTMLElement;
  updateTick(tick: Tick): void;
  setStreamStatus(status: StreamStatus): void;
  /** Re-render from the cached tick (e.g. after a scale switch). */
  rerender(): void;
}

export function buildBandReadoutView(bandWidthUsd: number, label: string): BandReadoutView {
  const statusEl = el("div", { class: "status" }, ["● Connecting..."]);
  const mainRatio = el("div", { class: "ratio-main" }, ["--.--"]);
  const btcPrice = el("div", { class: "btc-price-value" }, ["$--,---"]);
  const bidDepth = el("span", { class: "depth-value" }, ["--,---.-- BTC"]);
  const askDepth = el("span", { class: "depth-value" }, ["--,---.-- BTC"]);
  const range = el("div", { class: "stats-age" }, [""]);
  let lastTick: Tick | null = null;

  function renderTick(tick: Tick): void {
    lastTick = tick;
    mainRatio.textContent = formatDisplayRatio(tick.ratio);
    btcPrice.textContent = formatPrice(tick.mid_price);
    if (isEntitled(tick)) {
      setLiveValue(bidDepth, formatBtc(tick.bid_depth));
      setLiveValue(askDepth, formatBtc(tick.ask_depth));
    } else {
      // ADR-0013: same locked rendering as the main readout.
      setLockedValue(bidDepth);
      setLockedValue(askDepth);
    }
    // T-156 per-second true extremes, when present.
    range.textContent =
      tick.ratio_window_min != null && tick.ratio_window_max != null
        ? `1s range: ${formatDisplayRatio(tick.ratio_window_min)} – ${formatDisplayRatio(tick.ratio_window_max)}`
        : "";
  }

  const root = el(
    "div",
    { class: "dashboard-core research-readout", "data-band-width-usd": String(bandWidthUsd) },
    [
      el("div", { class: "header" }, [el("div", { class: "title research-title" }, [label]), statusEl]),
      el("div", { class: "ratio-section" }, [mainRatio]),
      el("div", { class: "btc-price-section" }, [el("div", { class: "btc-price-label" }, ["BITCOIN"]), btcPrice]),
      el("div", { class: "depth-section" }, [
        el("div", { class: "bid-depth" }, [el("span", { class: "depth-label" }, ["Bid Depth:"]), bidDepth]),
        el("div", { class: "ask-depth" }, [el("span", { class: "depth-label" }, ["Ask Depth:"]), askDepth]),
      ]),
      range,
    ]
  );

  return {
    root,
    updateTick: renderTick,
    setStreamStatus(status: StreamStatus): void {
      renderStreamStatus(statusEl, status);
    },
    rerender(): void {
      if (lastTick) renderTick(lastTick);
    },
  };
}
